import { useCallback, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DownloadIcon, PrinterIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnSecondary, inputCls } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { fetchAgenda } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { daysOfMonth, hoursLabel, staffHoursCsv, staffHoursMonth } from '../../core/rules/staffHours';
import { staffList, staffName } from '../../core/rules/salonDays';
import { dayShort, today } from '../../core/format/dates';
import { downloadCsv } from '../../utils/download';
import { printStaffHours } from './staffHoursDocument';
import type { AgendaRange } from '../../core/types/agenda';
const monthLabelOf = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('nl-NL', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Omzet & rapporten › Urenoverzicht per collega per maand (alleen Sarah): gewerkte salondagen met
 * tijden, afgeronde behandelingen, niet gekomen en geannuleerd apart, en totalen. Downloaden (CSV)
 * en printen, om naast de factuur van de collega te leggen. Geen omzet: alle omzet is van Beauty Bewust.
 */
export function StaffHoursPanel() {
  const id = useId();
  const {
    site
  } = useAdmin();
  const team = staffList(site.staff);
  const colleagues = team.filter(s => s.role === 'staff');
  const now = today();
  const [month, setMonth] = useState(now.slice(0, 7));
  const [who, setWho] = useState(colleagues[0]?.id ?? team[0].id);
  const days = daysOfMonth(month);
  const from = days[0];
  const to = days[days.length - 1];
  const load = useCallback((opts: {
    signal?: AbortSignal;
  }) => fetchAgenda(from, to, opts), [from, to]);
  const {
    data,
    status,
    reload
  } = useLatestLoad<AgendaRange>(load);
  const report = useMemo(() => data ? staffHoursMonth(site.openingHours, site.salonExceptions, data.appointments, who, month, now) : null, [data, site.openingHours, site.salonExceptions, who, month, now]);
  const name = staffName(team, who);
  const label = monthLabelOf(month);
  const print = async () => {
    if (report && !(await printStaffHours(report, name, label, site.salon.name))) toast.error('Printen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <Panel title="Urenoverzicht" description="Per maand: gewerkte dagen met tijden en behandelingen, om naast de factuur van je collega te leggen." actions={<div className="flex flex-wrap gap-2" data-id="element-3806" data-mp="z6mycd7bp9gy">
          <button type="button" onClick={() => report && downloadCsv(`uren-${name.toLowerCase()}-${month}.csv`, staffHoursCsv(report, name))} disabled={!report} className={btnSecondary} data-id="element-3807" data-mp="z9slhmwe61a1">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3808" data-mp="2amla4otvy9lv" />
            CSV
          </button>
          <button type="button" onClick={() => void print()} disabled={!report} className={btnSecondary} data-id="element-3809" data-mp="2eqawr8r02h49">
            <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3810" data-mp="3v4vtoijam1o3" />
            Printen
          </button>
        </div>} data-id="element-3805" data-mp="pb34dwcbkkuu">
      <div className="flex flex-wrap gap-4" data-id="element-3811" data-mp="3qcvz75lzs0yw">
        <label htmlFor={`${id}-who`} className="block" data-id="element-3812" data-mp="3lf3zhpf3vak5">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-3813" data-mp="3leks32n304kd">Medewerker</span>
          <select id={`${id}-who`} value={who} onChange={e => setWho(e.target.value)} className={`${inputCls} w-48`} data-id="element-3814" data-mp="3gh2fn29uv77m">
            {[...colleagues, ...team.filter(s => s.role === 'owner')].map(s => <option key={s.id} value={s.id} data-id="element-3815" data-mp="37g49oefuwpme">
                {s.name}
                {s.active ? '' : ' (gestopt)'}
              </option>)}
          </select>
        </label>
        <label htmlFor={`${id}-month`} className="block" data-id="element-3816" data-mp="15mwwbajt0i35">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-3817" data-mp="10ove0ru7cmsq">Maand</span>
          <input id={`${id}-month`} type="month" value={month} max={now.slice(0, 7)} onChange={e => e.target.value && setMonth(e.target.value)} className={`${inputCls} w-48`} data-id="element-3818" data-mp="10pmwd0ysgo12" />
        </label>
      </div>

      {status === 'error' && !data ? <LoadFailed variant="inline" title="De agenda laden lukte niet" onRetry={reload} className="mt-5" data-id="element-3819" data-mp="3kj6u1e1uvr83" /> : !report ? <LoadingLine className="mt-5" data-id="element-3820" data-mp="3pgfczgutvzae" /> : <>
          <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-3" data-id="element-3821" data-mp="3asc7e8kp6pm4">
            <div data-id="element-3822" data-mp="3aqrd8k2ap8os">
              <dt className="text-[12px] text-muted" data-id="element-3823" data-mp="3fjkhz7xrkh0v">Uren</dt>
              <dd className="font-display text-[1.8rem] font-light leading-none tabular-nums text-ink" data-id="element-3824" data-mp="3fl5ev47jwc1w">{hoursLabel(report.totals.minutes)}</dd>
            </div>
            <div data-id="element-3825" data-mp="35szlpr5kbol4">
              <dt className="text-[12px] text-muted" data-id="element-3826" data-mp="35ukgnhvsh9ji">Behandelingen afgerond</dt>
              <dd className="font-display text-[1.8rem] font-light leading-none tabular-nums text-ink" data-id="element-3827" data-mp="2rtq54qdg2sgm">{report.totals.done}</dd>
            </div>
            <div className="self-end text-[13px] text-muted" data-id="element-3828" data-mp="2wsb4yoosba4j">
              {report.totals.days} {report.totals.days === 1 ? 'dag' : 'dagen'} · {report.totals.noShow} niet gekomen · {report.totals.cancelled} geannuleerd
            </div>
          </dl>
          {report.days.length === 0 ? <p className="mt-5 text-[13px] text-muted" data-id="element-3829" data-mp="2n6s2uxz518e0">{name} heeft in {label} (nog) niet gewerkt.</p> : <div className="mt-5 overflow-x-auto" data-id="element-3830" data-mp="2i8qkygdg7f44">
              <table className="w-full text-[13px]" data-id="element-3831" data-mp="2w53sawo9q04v">
                <caption className="sr-only" data-id="element-3832" data-mp="2w3sqv3iap4gx">
                  Uren van {name} in {label}
                </caption>
                <thead className="text-left text-muted" data-id="element-3833" data-mp="35voyb569u6xt">
                  <tr className="border-b border-line" data-id="element-3834" data-mp="35v6r5di2m8b4">
                    <th scope="col" className="py-2 pr-3 font-normal" data-id="element-3835" data-mp="30wltnlu7e2qd">Dag</th>
                    <th scope="col" className="py-2 pr-3 font-normal" data-id="element-3836" data-mp="3ewdd423hobbr">Tijden</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3837" data-mp="3euac6n0ssxs9">Uren</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3838" data-mp="3asymkhhxcck2">Afgerond</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3839" data-mp="3hw53f1tfqkqb">Niet gekomen</th>
                    <th scope="col" className="py-2 text-right font-normal" data-id="element-3840" data-mp="3mv89mpl5iwzv">Geannuleerd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line tabular-nums text-ink" data-id="element-3841" data-mp="hhwxfga3h4o">
                  {report.days.map(d => <tr key={d.date} data-id="element-3842" data-mp="4ibjo5hzipvz">
                      <td className="py-2 pr-3 capitalize" data-id="element-3843" data-mp="4lfsjg6ea9sz">
                        {dayShort(d.date)}
                        {d.exception && <span className="ml-1 text-[12px] normal-case text-muted" data-id="element-3844" data-mp="ehj4fdr1tmt4">afwijkend</span>}
                      </td>
                      <td className="py-2 pr-3" data-id="element-3845" data-mp="jgvrnzlu0vw8">
                        {d.open}–{d.close}
                      </td>
                      <td className="py-2 pr-3 text-right" data-id="element-3846" data-mp="2ga7cq1ijv0fr">{hoursLabel(d.minutes)}</td>
                      <td className="py-2 pr-3 text-right" data-id="element-3847" data-mp="2l90g3144hb27">{d.done}</td>
                      <td className="py-2 pr-3 text-right" data-id="element-3848" data-mp="12cm997khlfft">{d.noShow}</td>
                      <td className="py-2 text-right" data-id="element-3849" data-mp="12eywo090ki09">{d.cancelled}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </>}
    </Panel>;
}